import { queryKeys } from '../keys'
import { DEFAULT_RETRY_MUTATION } from '../config'
import { transformTransportation } from '../selects/transportation'
import { transformBooking } from '../selects'
import type { QueryClient } from '@tanstack/react-query'
import type {
  GetBookingDetailResponse,
  GetBookingTransportationResponse,
} from '@/types/booking'
import type { UpdateSelectedDateSchema } from '@/schemas/booking'
import { bookingService } from '@/services/booking'

function searchBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.search(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.search>[0]) =>
      bookingService.search(payload),
  })
}

function createBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.create(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.create>[0]) =>
      bookingService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.myBooking.tabList(),
      })
    },
  })
}

function updateBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.update(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.update>[0]) =>
      bookingService.update(payload),
    onSuccess: async (res, payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: res.data,
              },
      )
    },
  })
}

function updateBookingInfo(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.updateInfo(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.updateInfo>[0]) =>
      bookingService.updateInfo(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
        )
      if (!existingBooking) return
      const optimisticBooking = {
        ...existingBooking,
        data: {
          ...existingBooking.data,
          booking: {
            ...existingBooking.data.booking,
            contact_email: payload.adhoc_booking.contact_email,
            contact_name: payload.adhoc_booking.contact_name,
            contact_phone: payload.adhoc_booking.contact_phone,
          },
        },
      } satisfies typeof existingBooking
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        optimisticBooking,
      )
      return { existingBooking }
    },
    onSuccess: (res, payload) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: res.data,
              },
      )
    },
    onError: (_, payload, context) => {
      if (!context?.existingBooking) return
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        context?.existingBooking,
      )
    },
  })
}

function deleteTransportationBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.deleteTransportation(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<typeof bookingService.deleteTransportation>[0],
      ) => bookingService.deleteTransportation(payload),
      onSuccess: (_, payload) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.detail(payload.bookingId),
        })
      },
    },
  )
}

function claimBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.claim(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.claim>[0]) =>
      bookingService.claim(payload),
  })
}

function applyVoucher(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.applyVoucher(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.applyVoucher>[0]) =>
      bookingService.applyVoucher(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
    onSuccess: (res, payload) => {
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
        )
      if (!existingBooking) return
      const newBooking: GetBookingDetailResponse = {
        ...existingBooking,
        data: {
          ...existingBooking,
          booking: {
            ...existingBooking.data.booking,
            voucher: res.data.booking.voucher,
            voucher_amount: res.data.booking.voucher_amount,
            voucher_amount_in_currency:
              res.data.booking.voucher_amount_in_currency,
            voucher_amount_type: res.data.booking.voucher_amount_type,
            sub_total: res.data.booking.sub_total,
            tax_amount: res.data.booking.tax_amount,
            tax_name: res.data.booking.tax_name,
            total_price: res.data.booking.total_price,
          },
        },
      }
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        newBooking,
      )
    },
  })
}

function deleteVoucher(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.deleteVoucher(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.deleteVoucher>[0]) =>
      bookingService.deleteVoucher(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
    onSuccess: (res, payload) => {
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
        )
      if (!existingBooking) return
      const newBooking: GetBookingDetailResponse = {
        ...existingBooking,
        data: {
          ...existingBooking,
          booking: {
            ...existingBooking.data.booking,
            voucher: res.data.booking.voucher,
            voucher_amount: res.data.booking.voucher_amount,
            voucher_amount_in_currency:
              res.data.booking.voucher_amount_in_currency,
            voucher_amount_type: res.data.booking.voucher_amount_type,
            sub_total: res.data.booking.sub_total,
            tax_amount: res.data.booking.tax_amount,
            tax_name: res.data.booking.tax_name,
            total_price: res.data.booking.total_price,
          },
        },
      }
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        newBooking,
      )
    },
  })
}

function checkoutBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.checkout(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.checkout>[0]) =>
      bookingService.checkout(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
  })
}

function cancelBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.cancel(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.cancel>[0]) =>
      bookingService.cancel(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
    onSuccess: (res, payload) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: {
                  ...oldData.data,
                  booking: transformBooking({
                    ...oldData.data.booking,
                    ...res.data.booking,
                  }),
                },
              },
      )
    },
  })
}
function updateBookingBus(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.updateBus(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (
      payload: Parameters<typeof bookingService.updateTransportation>[0],
    ) => bookingService.updateTransportation(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.transportation(payload.id),
      })
    },
  })
}

function updateBookingTransportationTripType(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationTripType(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<
          typeof bookingService.updateTransportationTripType
        >[0],
      ) => bookingService.updateTransportationTripType(payload),
      onMutate: async (payload) => {
        await queryClient.cancelQueries({
          queryKey: queryKeys.booking.transportation(payload.id),
        })
      },
      onSuccess: (res, payload) => {
        const bus = queryClient.getQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
        )
        bus?.data?.booked_transportation?.adhoc_booking_id &&
          queryClient.invalidateQueries({
            queryKey: queryKeys.booking.detail(
              bus.data.booked_transportation.adhoc_booking_id,
            ),
          })
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booked_transportation: transformTransportation(
                      res.data.booked_transportation,
                    ),
                  },
                },
        )
      },
    },
  )
}

function cancelUnpaidBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.cancelUnpaid(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.cancel>[0]) =>
      bookingService.cancelUnpaid(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
    onSuccess: (res, payload) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: {
                  ...oldData.data,
                  booking: transformBooking({
                    ...oldData.data.booking,
                    ...res.data.booking,
                  }),
                },
              },
      )
    },
  })
}

function updateBookingTransportationSelectedDate(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationSelectedDate(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<
          typeof bookingService.updateTransportationSelectedDate
        >[0],
      ) => bookingService.updateTransportationSelectedDate(payload),
      onMutate: async (payload) => {
        await queryClient.cancelQueries({
          queryKey: queryKeys.booking.transportation(payload.id),
        })
      },
      onSuccess: (res, payload) => {
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booked_transportation: transformTransportation({
                      ...old.data.booked_transportation,
                      ...res.data.booked_transportation,
                    }),
                  },
                },
        )
      },
    },
  )
}

function updateBookingTransportationSelectedTime(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationSelectedTime(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<
          typeof bookingService.updateTransportationSelectedTime
        >[0],
      ) => bookingService.updateTransportationSelectedTime(payload),
      onMutate: async (payload) => {
        await queryClient.cancelQueries({
          queryKey: queryKeys.booking.transportation(payload.id),
        })
      },
      onSuccess: (res, payload) => {
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booked_transportation: transformTransportation({
                      ...old.data.booked_transportation,
                      ...res.data.booked_transportation,
                    }),
                  },
                },
        )
      },
    },
  )
}

function patchBookingRoutesPoints(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationRoutesPoints(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<typeof bookingService.patchBookingPointsRoutes>[0],
      ) => bookingService.patchBookingPointsRoutes(payload),
      onSuccess: (res, payload) => {
        const currentBus =
          queryClient.getQueryData<GetBookingTransportationResponse>(
            queryKeys.booking.transportation(payload.id),
          )

        if (!currentBus) return

        const optimisticBus: GetBookingTransportationResponse = {
          ...currentBus,
          data: {
            ...currentBus.data,
            booked_transportation: {
              ...currentBus.data.booked_transportation,
              ...res.data.booked_transportation,
              ...(currentBus.data.booked_transportation
                .booked_transportation_point_to_points?.length
                ? {
                    booked_transportation_point_to_points:
                      res.data.booked_transportation
                        .booked_transportation_point_to_points,
                  }
                : {}),
              ...(currentBus.data.booked_transportation
                .booked_transportation_hourly
                ? {
                    booked_transportation_hourly:
                      res.data.booked_transportation
                        .booked_transportation_hourly,
                  }
                : {}),
            },
          },
        }
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          optimisticBus,
        )
      },
    },
  )
}

function patchBookingRoutesHourly(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationRoutesHourly(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: (
        payload: Parameters<typeof bookingService.patchBookingHourlyRoutes>[0],
      ) => bookingService.patchBookingHourlyRoutes(payload),
      onSuccess: (_, payload) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.transportation(payload.id),
        })
      },
    },
  )
}

function calculateCancelBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.calculateCancel(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (
      payload: Parameters<typeof bookingService.calculateCancel>[0],
    ) => bookingService.calculateCancel(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.id),
      })
    },
    onSuccess: (res) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(res.data.booking.id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: {
                  ...oldData.data,
                  ...res.data,
                  booking: transformBooking({
                    ...oldData.data.booking,
                    ...res.data.booking,
                    adhoc_booking_booked_transportations:
                      oldData.data.booking.adhoc_booking_booked_transportations.map(
                        (bus) => ({
                          ...bus,
                          ...(res.data.booking.adhoc_booking_booked_transportations.find(
                            (b) => b.id === bus.id,
                          ) ?? {}),
                        }),
                      ),
                  }),
                },
              },
      )
    },
  })
}

function postAmendBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.amend(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.amend>[0]) =>
      bookingService.amend(payload),
  })
}

function postSelectBusPlan(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.selectPlan(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.selectPlan>[0]) =>
      bookingService.selectPlan(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.booking_id),
      })
    },
    onSuccess: (res, payload) => {
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.booking_id),
        )
      if (!existingBooking) return
      const optimisticBooking: GetBookingDetailResponse = {
        ...existingBooking,
        data: {
          ...existingBooking.data,
          booking: {
            ...existingBooking.data.booking,
            sub_total: res.data.sub_total,
            voucher_amount_in_currency: res.data.voucher_amount_in_currency,
            total_price: res.data.total_price,
            ...(res.data.tax_amount && +res.data.tax_amount && res.data.tax_name
              ? { tax_amount: res.data.tax_amount, tax_name: res.data.tax_name }
              : {}),
          },
        },
      }
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.booking_id),
        optimisticBooking,
      )
    },
  })
}

function postSubmitAmend(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.booking.submitAmend(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof bookingService.submitAmend>[0]) =>
      bookingService.submitAmend(payload),
    onSuccess: (res, payload) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.id),
        (oldData) =>
          !oldData
            ? res
            : {
                ...oldData,
                data: {
                  ...oldData.data,
                  booking: transformBooking({
                    ...oldData.data.booking,
                    ...res.data.booking,
                  }),
                },
              },
      )
    },
  })
}

function getCalculatePointRoutes(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.postBookingPointsRoutes(),
    {
      retry: 0,
      mutationFn: (
        params: Parameters<typeof bookingService.postBookingPointsRoutes>[0],
      ) => bookingService.postBookingPointsRoutes(params),
    },
  )
}

function getCalculateTransportationDate(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.transportationSelectedDate(),
    {
      retry: 0,
      mutationFn: (payload: UpdateSelectedDateSchema) =>
        bookingService.getSelectedDate(payload.id, payload.date),
    },
  )
}

function getCalculateTransportationTime(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.transportationSelectedTime(),
    {
      retry: 0,
      mutationFn: (
        payload: Parameters<
          typeof bookingService.getTransportationSelectedTime
        >[0],
      ) => bookingService.getTransportationSelectedTime(payload),
    },
  )
}

function updateTransportationPassenger(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(
    queryKeys.booking.updateTransportationPassenger(),
    {
      retry: 0,
      mutationFn: (
        payload: Parameters<
          typeof bookingService.updateTransportationPassenger
        >[0],
      ) => bookingService.updateTransportationPassenger(payload),
      onMutate: async (payload) => {
        await queryClient.cancelQueries({
          queryKey: queryKeys.booking.transportation(payload.id),
        })
      },
      onSuccess: (_, payload) => {
        const existingTransportation =
          queryClient.getQueryData<GetBookingTransportationResponse>(
            queryKeys.booking.transportation(payload.id),
          )
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booked_transportation: transformTransportation({
                      ...old.data.booked_transportation,
                      passenger_quantity:
                        payload.payload.adhoc_booking_booked_transportation
                          .passenger_quantity,
                    }),
                  },
                },
        )
        if (
          existingTransportation?.data.booked_transportation.adhoc_booking_id
        ) {
          queryClient.setQueryData<GetBookingDetailResponse>(
            queryKeys.booking.detail(
              existingTransportation.data.booked_transportation
                .adhoc_booking_id,
            ),
            (old) =>
              !old
                ? old
                : {
                    ...old,
                    data: {
                      ...old.data,
                      booking: transformBooking({
                        ...old.data.booking,
                        adhoc_booking_booked_transportations:
                          old.data.booking.adhoc_booking_booked_transportations.map(
                            (bus) =>
                              bus.id === payload.id
                                ? transformTransportation({
                                    ...bus,
                                    passenger_quantity:
                                      payload.payload
                                        .adhoc_booking_booked_transportation
                                        .passenger_quantity,
                                  })
                                : bus,
                          ),
                      }),
                    },
                  },
          )
        }
      },
    },
  )
}

export function registerBookingMutations(queryClient: QueryClient) {
  searchBooking(queryClient)
  createBooking(queryClient)
  updateBooking(queryClient)
  updateBookingInfo(queryClient)
  deleteTransportationBooking(queryClient)
  claimBooking(queryClient)
  applyVoucher(queryClient)
  deleteVoucher(queryClient)
  checkoutBooking(queryClient)
  cancelBooking(queryClient)
  cancelUnpaidBooking(queryClient)
  calculateCancelBooking(queryClient)
  updateBookingBus(queryClient)
  updateBookingTransportationTripType(queryClient)
  updateBookingTransportationSelectedDate(queryClient)
  updateBookingTransportationSelectedTime(queryClient)
  patchBookingRoutesPoints(queryClient)
  patchBookingRoutesHourly(queryClient)
  postAmendBooking(queryClient)
  postSelectBusPlan(queryClient)
  postSubmitAmend(queryClient)
  getCalculatePointRoutes(queryClient)
  getCalculateTransportationDate(queryClient)
  getCalculateTransportationTime(queryClient)
  updateTransportationPassenger(queryClient)
}
